import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  CheckCircleIcon, XCircleIcon, ClockIcon, AlertTriangleIcon,
  ShieldCheckIcon, RefreshCwIcon, UserIcon,
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getApiEndpoint } from "@/lib/api";
import { getToken, getUser } from "@/lib/auth-storage";
import { useToast } from "@/hooks/use-toast";

interface Approval {
  id: number;
  ticket_id: string;
  ticket_title: string;
  ticket_type: string;
  priority: string;
  ticket_status: string;
  ticket_created_at: string;
  approver_id: number;
  approver_name?: string;
  approval_type: string;
  status: string;
  order_seq: number;
  notes?: string;
  responded_at?: string;
  deadline?: string;
  requester_name?: string;
  change_category?: string;
  risk_level?: string;
}

interface Summary {
  pending: number;
  approved: number;
  rejected: number;
  my_pending: number;
}

const PRIORITY_COLORS: Record<string, string> = {
  critical: "bg-red-100 text-red-800",
  high: "bg-orange-100 text-orange-800",
  medium: "bg-yellow-100 text-yellow-800",
  low: "bg-green-100 text-green-800",
};
const PRIORITY_LABELS: Record<string, string> = {
  critical: "Crítica", high: "Alta", medium: "Média", low: "Baixa",
};
const fmtDate = (iso?: string) => iso ? new Date(iso).toLocaleDateString("pt-BR", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" }) : "—";

const ApprovalQueue = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [myApprovals, setMyApprovals] = useState<Approval[]>([]);
  const [summary, setSummary] = useState<Summary>({ pending: 0, approved: 0, rejected: 0, my_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<number | null>(null);
  const [actionType, setActionType] = useState<"approve" | "reject">("approve");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const token = getToken();
  const user = getUser() ? JSON.parse(getUser()!) : null;

  useEffect(() => {
    if (!user) { navigate("/login"); return; }
    loadAll();
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [mRes, sRes] = await Promise.all([
        fetch(getApiEndpoint("/api/approvals/mine"), { headers: { Authorization: `Bearer ${token}` } }),
        fetch(getApiEndpoint("/api/approvals/summary"), { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (mRes.ok) { const d = await mRes.json(); setMyApprovals(d.approvals || []); }
      if (sRes.ok) { const d = await sRes.json(); setSummary(d.summary || { pending: 0, approved: 0, rejected: 0, my_pending: 0 }); }
    } finally {
      setLoading(false);
    }
  }, [token]);

  const openAction = (id: number, type: "approve" | "reject") => {
    setActionId(id);
    setActionType(type);
    setNotes("");
  };

  const handleSubmit = async () => {
    if (actionType === "reject" && !notes.trim()) {
      toast({ title: "Justificativa obrigatória", description: "Informe o motivo da rejeição", variant: "destructive" });
      return;
    }
    setSubmitting(true);
    const res = await fetch(
      getApiEndpoint(`/api/approvals/${actionId}/${actionType === "approve" ? "approve" : "reject"}`),
      {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ notes: notes || null }),
      }
    );
    setSubmitting(false);
    if (res.ok) {
      toast({
        title: actionType === "approve" ? "Aprovado" : "Rejeitado",
        description: actionType === "approve" ? "Aprovação registrada com sucesso" : "Rejeição registrada",
      });
      setActionId(null);
      loadAll();
    } else {
      const err = await res.json().catch(() => ({}));
      toast({ title: "Erro", description: err.error || "Não foi possível processar", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex flex-col">
      <Header />
      <main className="flex-1 w-full max-w-6xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
              <ShieldCheckIcon className="h-6 w-6 text-green-600" /> Fila de Aprovações
            </h1>
            <p className="text-gray-500 text-sm mt-1">Aprovações CAB, técnicas e de negócio</p>
          </div>
          <Button onClick={loadAll} variant="outline" size="sm" className="gap-2">
            <RefreshCwIcon className="h-4 w-4" /> Atualizar
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: "Aguardando (geral)", value: summary.pending, icon: ClockIcon, color: "yellow" },
            { label: "Minhas Pendências", value: summary.my_pending, icon: UserIcon, color: "orange" },
            { label: "Aprovadas", value: summary.approved, icon: CheckCircleIcon, color: "green" },
            { label: "Rejeitadas", value: summary.rejected, icon: XCircleIcon, color: "red" },
          ].map((m, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs text-gray-500">{m.label}</p>
                    <p className={`text-2xl font-bold text-${m.color}-600`}>{loading ? "—" : m.value}</p>
                  </div>
                  <m.icon className={`h-8 w-8 text-${m.color}-200`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Pending Approvals */}
        <div className="space-y-3">
          {myApprovals.length === 0 && !loading ? (
            <div className="text-center text-gray-400 py-16">
              <ShieldCheckIcon className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium">Nenhuma aprovação pendente</p>
              <p className="text-sm mt-1">Você está em dia com suas aprovações</p>
            </div>
          ) : (
            myApprovals.map(a => (
              <Card key={a.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <Link to={`/atendimento/chamado/${a.ticket_id}`} className="font-semibold text-sm hover:text-blue-600 truncate">
                          {a.ticket_title}
                        </Link>
                        <span className="text-xs text-gray-400 shrink-0">{a.ticket_id}</span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Badge className={`text-xs ${PRIORITY_COLORS[a.priority] || ""} border-0`}>
                          {PRIORITY_LABELS[a.priority] || a.priority}
                        </Badge>
                        <Badge variant="outline" className="text-xs capitalize">
                          {a.ticket_type === "change" ? "Mudança" : a.ticket_type === "incident" ? "Incidente" : a.ticket_type === "request" ? "Requisição" : "Problema"}
                        </Badge>
                        <Badge variant="outline" className="text-xs capitalize">
                          {a.approval_type === "cab" ? "CAB" : a.approval_type === "technical" ? "Técnica" : a.approval_type === "business" ? "Negócio" : "Segurança"}
                        </Badge>
                        {a.change_category && (
                          <Badge variant="outline" className="text-xs">
                            {a.change_category === "emergency" ? "⚠ Emergência" : a.change_category === "standard" ? "Padrão" : "Normal"}
                          </Badge>
                        )}
                        {a.risk_level && (
                          <Badge variant="outline" className={`text-xs ${a.risk_level === "critical" || a.risk_level === "high" ? "border-red-300 text-red-700" : ""}`}>
                            Risco {a.risk_level}
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-gray-400 mt-2">
                        Solicitante: {a.requester_name || "—"} • Criado: {fmtDate(a.ticket_created_at)}
                        {a.deadline && ` • Prazo: ${fmtDate(a.deadline)}`}
                      </p>
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-green-300 text-green-700 hover:bg-green-50 gap-1"
                        onClick={() => openAction(a.id, "approve")}
                      >
                        <CheckCircleIcon className="h-4 w-4" /> Aprovar
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="border-red-300 text-red-700 hover:bg-red-50 gap-1"
                        onClick={() => openAction(a.id, "reject")}
                      >
                        <XCircleIcon className="h-4 w-4" /> Rejeitar
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </main>

      {/* Confirm Dialog */}
      <Dialog open={actionId !== null} onOpenChange={open => { if (!open) setActionId(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={actionType === "approve" ? "text-green-700" : "text-red-700"}>
              {actionType === "approve" ? "Confirmar Aprovação" : "Confirmar Rejeição"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {actionType === "approve" ? (
              <p className="text-sm text-gray-600">
                Você está prestes a <strong>aprovar</strong> esta solicitação. Adicione observações se necessário.
              </p>
            ) : (
              <p className="text-sm text-gray-600">
                Você está prestes a <strong>rejeitar</strong> esta solicitação. Informe o motivo da rejeição (obrigatório).
              </p>
            )}
            <div>
              <label className="text-sm font-medium">
                {actionType === "approve" ? "Observações (opcional)" : "Motivo da rejeição *"}
              </label>
              <Textarea
                value={notes}
                onChange={e => setNotes(e.target.value)}
                placeholder={actionType === "approve" ? "Adicione um comentário se desejar..." : "Descreva o motivo da rejeição..."}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setActionId(null)} disabled={submitting}>Cancelar</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className={actionType === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}
            >
              {submitting ? "Processando..." : actionType === "approve" ? "Confirmar Aprovação" : "Confirmar Rejeição"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Footer />
    </div>
  );
};

export default ApprovalQueue;
